import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import {
	AbstractControl,
	FormBuilder,
	FormGroup,
	Validators,
} from '@angular/forms';
import { Store } from '@ngrx/store';
import { take } from 'rxjs/operators';
import { TYPE_TOAST } from 'src/app/models/enumeraciones';
import { OtpValidationError } from 'src/app/models/auth/auth-token.models';
import { TdcEvaluationResult } from 'src/app/models/tdc/tdc-evaluation.model';
import { GoToNextStep, GoToRejectedStep } from 'src/app/models/utils/steps';
import { TdcApiService } from 'src/app/services/apis/tdc/tdc-api.service';
import { EVALUATION_TOKEN_MEDIA_TYPE } from 'src/app/config/auth-recovery.config';
import { ExternalAuthService } from 'src/app/services/auth/external-auth.service';
import { OtpReauthenticationService } from 'src/app/services/auth/otp-reauthentication.service';
import { EnterCodeComponent } from 'src/app/shared/components/enter-code/enter-code.component';
import { MonitoringService } from 'src/app/services/monitoring/monitoring.service';
import { OnboardingProcessFlowService } from 'src/app/services/onboarding-process-flow.service';
import { ConfirmModalService } from 'src/app/services/utils/confirm-modal.service';
import { FullSizeLoadingService } from 'src/app/services/utils/full-size-loading.service';
import { UtilsService } from 'src/app/services/utils/utils.service';
import { PreApplicationActions } from 'src/app/state/actions/pre-application.actions';
import {
	selectApplicantPhoneAndIsApplicantAMember,
	selectPreAplicationState,
} from 'src/app/state/selectors/pre-application.selectors';

@Component({
	selector: 'cmv-credit-otp-code',
	templateUrl: './credit-otp-code.component.html',
	styleUrls: ['./credit-otp-code.component.scss'],
})
export class CreditOtpCodeComponent
	implements OnInit, GoToNextStep, GoToRejectedStep
{
	isCodevalid: boolean = false;
	applicantPhoneAndIsApplicantAMember$ = this.store.select(
		selectApplicantPhoneAndIsApplicantAMember
	);
	modalContainer: string = '#cmvloadingmodal';
	otpCode: string = '';
	acceptTermsForm: FormGroup;
	confirmOtpForm: FormGroup;
	@ViewChild('acceptTermsInput')
	acceptTermsInput: ElementRef<HTMLInputElement>;
	@ViewChild('enterCodeRef') enterCodeRef: EnterCodeComponent;
	statusToValidateTdc: number[] = [1, 2, 4];

	constructor(
		private store: Store,
		private utilsService: UtilsService,
		private confirmModalService: ConfirmModalService,
		private onboardingProcessFlowService: OnboardingProcessFlowService,
		private formBuilder: FormBuilder,
		private tdcApiService: TdcApiService,
		private monitoringService: MonitoringService,
		private fullSizeLoadingService: FullSizeLoadingService,
		private externalAuthService: ExternalAuthService,
		private otpReauthentication: OtpReauthenticationService
	) {}

	goToRejected(message: string = null, code: string = null): void {
		this.onboardingProcessFlowService.goToRejected(message, code);
	}

	goToNextStep(): void {
		this.onboardingProcessFlowService.goToNextStep();
	}

	ngOnInit(): void {
		this.acceptTermsForm = this.formBuilder.group({
			acceptTerms: [null, [Validators.requiredTrue]],
			acceptGeographicalLocation: [null, [Validators.requiredTrue]],
		});
	}

	get acceptTermsField(): AbstractControl {
		return this.acceptTermsForm.get('acceptTerms');
	}

	get acceptGeographicalLocationField(): AbstractControl {
		return this.acceptTermsForm.get('acceptGeographicalLocation');
	}

	onContinuar(): void {
		if (!this.isCodevalid) {
			this.utilsService.ShowToast(
				'Necesita validar el codigo que se envio a su celular',
				TYPE_TOAST.WARNING,
				10000
			);
			return;
		}

		if (this.acceptTermsForm.invalid) {
			this.acceptTermsField.markAsDirty();
			this.acceptGeographicalLocationField.markAsDirty();
			return;
		}

		// El socio no está pasando por el paso 3: volvió aquí solo porque el token
		// de evaluación venció. Regresa a la pantalla donde estaba en lugar de
		// avanzar al siguiente paso, que ya dejó atrás.
		if (this.otpReauthentication.isPending) {
			this.otpReauthentication.resume();
			return;
		}

		this.checkProductBeforeContinue();
	}

	showTerms(): void {
		this.confirmModalService
			.open(this.modalContainer, {
				message:
					'Autorizo expresamente a Caja Morelia Valladolid, S.C. de A.P. de R.L. de C.V., para lleve a cabo Investigaciones, sobre mi comportamiento Crediticio en SIC que estime conveniente. Conozco la naturaleza y alcance de la información que se solicitará, del uso que se le dará y que se podrá realizar consultas periódicas de mi historial crediticio, consiento que esta autorización tenga vigencia de 3 años contados a partir de hoy, y en su caso mientras mantengamos relación jurídica.',
				buttonText: 'Autorizar',
			})
			.subscribe({
				next: (confirmed: boolean) => {
					if (confirmed) {
						this.acceptTermsField.setValue(true);
					}
				},
				error: error => {
					console.log(
						'🚀 ~ file: socio-economic-data.component.ts:332 ~ SocioEconomicDataComponent ~ this.confirmModalService.open ~ error:',
						error
					);
				},
			});
	}

	onSuccess(otpCode: string): void {
		this.store
			.select(selectPreAplicationState)
			.pipe(take(1))
			.subscribe(state => {
				this.externalAuthService
					.fetchEvaluationToken(
						otpCode,
						String(state.applicantId),
						EVALUATION_TOKEN_MEDIA_TYPE
					)
					.subscribe({
						next: () => {
							this.enterCodeRef?.markAsValidated();
							this.isCodevalid = true;
							this.store.dispatch(PreApplicationActions.addOtpCode({ otpCode }));
							this.otpCode = otpCode;
							this.focusElement(this.acceptTermsInput);
						},
						error: error => {
							this.enterCodeRef?.resetAfterExternalValidationError();
							if (error instanceof OtpValidationError) {
								this.utilsService.ShowToast(error.message, TYPE_TOAST.ERROR);
							} else {
								this.utilsService.ShowToast(
									'Error de conexión, intente de nuevo',
									TYPE_TOAST.ERROR
								);
							}
						},
					});
			});
	}

	onShowEnterCodeError(error: string) {
		this.utilsService.ShowToast(error, TYPE_TOAST.WARNING);
	}

	onShowGeneratedOtpMessage(message: string) {
		this.utilsService.ShowToast(message);
	}

	private focusElement(elementRef: ElementRef) {
		setTimeout(() => elementRef.nativeElement.focus());
	}

	private checkProductBeforeContinue() {
		this.store
			.select(selectPreAplicationState)
			.pipe(take(1))
			.subscribe(
				({
					productId,
					isApplicantAMember,
					preApplicationStatus,
					preApplicationId,
				}) => {
					const isProductTdc = productId === 3;
					const applicationNeedsToBeEvaluated =
						this.statusToValidateTdc.includes(preApplicationStatus);
					if (
						isProductTdc &&
						isApplicantAMember &&
						applicationNeedsToBeEvaluated
					) {
						this.evaluateTdcPreApplication(preApplicationId);
						return;
					}
					this.goToNextStep();
				}
			);
	}

	private evaluateTdcPreApplication(preApplicationId: number) {
		this.fullSizeLoadingService.open('Evaluando datos...');
		this.tdcApiService.evaluateTdcPreApplication(preApplicationId).subscribe({
			next: (tdcEvaluation: TdcEvaluationResult) => {
				this.fullSizeLoadingService.close();
				if (!tdcEvaluation.hasAnyCardApprobed) {
					this.monitoringService.captureError(
						new Error('Tarjeta de credito no pre aprobada'),
						{
							observaciones: tdcEvaluation.rejectionObservations,
						}
					);
					this.goToRejected(tdcEvaluation.rejectionObservations.join(','));
					return;
				}
				this.goToNextStep();
			},
			error: error => {
				this.fullSizeLoadingService.close();
				console.log('🚀 ~ evaluateTdcPreApplication ~ error:', error);
			},
		});
	}
}
